import { type ReactElement } from 'react'
import { TStack } from 'app/components/TStack'
import { type CompanyData, type TeamId, isNotEmpty } from '@terros/common'
import { useDisclosure } from '@mantine/hooks'
import { Collapse, Divider } from '@mantine/core'
import { type Team, SizePrimitives } from '@front/model'
import { type TeamItemProps, TeamItemWithoutChildren } from './TeamItem'

type TeamListProps = {
  company: CompanyData
  open?: boolean
  teams: Team[]
  itemProps?: Omit<TeamItemProps, 'company' | 'parentOpen' | 'team'>
  selectedTeams?: TeamId[]
  expandedTeamIds?: TeamId[]
  activeTeamId?: TeamId
}

export const TeamList = ({
  company,
  open = true,
  teams,
  itemProps,
  selectedTeams,
  expandedTeamIds,
  activeTeamId,
}: TeamListProps): ReactElement => {
  return (
    <Collapse in={open}>
      <TStack divider={<Divider />}>
        {teams.map((team) => (
          <TeamItem
            {...itemProps}
            activeTeamId={activeTeamId}
            company={company}
            expandedTeamIds={expandedTeamIds}
            key={team.teamId}
            parentOpen={open}
            selectedTeams={selectedTeams}
            team={team}
          />
        ))}
      </TStack>
    </Collapse>
  )
}

export const TeamItem = (props: TeamItemProps): ReactElement => {
  const {
    parentOpen,
    team,
    company,
    showManagers,
    onSelect,
    selectedTeams,
    expandedTeamIds,
    activeTeamId,
    ...itemProps
  } = props
  const [manuallyOpen, { toggle }] = useDisclosure()
  const open = manuallyOpen || Boolean(expandedTeamIds?.includes(team.teamId))

  return (
    <>
      <TeamItemWithoutChildren open={open} toggle={toggle} {...props} />
      <div style={{ paddingLeft: SizePrimitives['5'] }}>
        {isNotEmpty(team.children) && (
          <TeamList
            activeTeamId={activeTeamId}
            company={company}
            expandedTeamIds={expandedTeamIds}
            itemProps={{ showManagers, onSelect, ...itemProps }}
            open={open}
            selectedTeams={selectedTeams}
            teams={team.children}
          />
        )}
      </div>
    </>
  )
}
